import type { ParsedDictationChunk } from '../createNvoqWebsocketFactory';
import { createTextContainsChecker } from './textUtils';
import type { Editor } from 'slate';
import { endsInDigitAndIsNextNumber, isTextAndEndsInDigit } from './digitAndDecimalUtils';
import type { StepEntry } from '../useSteps';

export const PICKLIST_COMMAND_WORDS = ['pick', 'option', 'choose'] as const;
export const PICKLIST_COMMAND_ALIASES = ['PICC', 'chews'] as const;
export const PICKLIST_COMMAND_WORD_SET_LOWER = new Set(
  PICKLIST_COMMAND_WORDS.map((word) => word.toLowerCase().trim())
);
export const PICKLIST_COMMAND_ALIAS_SET_LOWER = new Set(
  PICKLIST_COMMAND_ALIASES.map((alias) => alias.toLowerCase().trim())
);

// Field command trigger word
export const GO_TO_FIELD_TRIGGER_WORD = 'field';

// Cached picklist keywords to avoid repeated array operations
const PICKLIST_KEYWORDS_LOWER = [
  ...PICKLIST_COMMAND_WORD_SET_LOWER,
  ...PICKLIST_COMMAND_ALIAS_SET_LOWER,
];

/**
 * Checks if a dictation chunk contains the field keyword
 */
export const isTextAndContainsFieldKeyword = createTextContainsChecker((text) =>
  text.includes(GO_TO_FIELD_TRIGGER_WORD)
);

/**
 * Checks if a dictation chunk ends with the field keyword
 */
export const isTextAndEndsInFieldKeyword = createTextContainsChecker((text) =>
  text.endsWith(GO_TO_FIELD_TRIGGER_WORD)
);

/**
 * Checks if a dictation chunk contains a picklist keyword or nVoq alias (e.g. "picc", "chews").
 * Aliases ensure "PICC 1" / "chews 1" are routed to the picklist handler for normalization.
 */
export const isTextAndContainsPicklistKeyword = createTextContainsChecker((text) =>
  PICKLIST_KEYWORDS_LOWER.some((keyword: string) => text.includes(keyword))
);

/**
 * Checks if a dictation chunk ends with a picklist keyword or alias (e.g. "pick", "picc", "chews").
 */
export const isTextAndEndsInPicklistKeyword = createTextContainsChecker((text) =>
  PICKLIST_KEYWORDS_LOWER.some((keyword: string) => text.endsWith(keyword))
);

/**
 * Checks if the step entries are text and ends in a digit but not a potential picklist command.
 * There are cases where picklist commands and digits can clash, with priority given to picklist commands.
 *
 * E.g. text of "2" should be deferred
 *      text of "banana 2" should be deferred
 *      text of "pick 2" should not be deferred because it's a potential picklist command
 *
 */
export const isTextAndEndsInDigitButNotPotentialPicklistCommand = (
  stepEntries: StepEntry[],
  i: number,
  editor: Editor
) => {
  const stepEntry = stepEntries[i];

  if (!isTextAndEndsInDigit(stepEntry[1])) {
    return false;
  }

  if (isTextAndContainsPicklistKeyword(stepEntry[1])) {
    return false;
  }

  // if the previous step contains a picklist keyword, then we should not defer
  if (stepEntries[i - 1] != null && isTextAndContainsPicklistKeyword(stepEntries[i - 1][1])) {
    return false;
  }

  // if the previous step isn't likely to be relevant for a
  // numbered list based on current editor selection, then we should not defer
  if (!endsInDigitAndIsNextNumber(stepEntry[1], editor)) {
    return false;
  }

  return true;
};

/**
 * Checks if a dictation chunk is a macro voice command
 */
export const isMacroVoiceCommand = (item: ParsedDictationChunk): boolean =>
  item.type === 'SUBSTITUTION' && (item.payload as any).action === 'InsertMacro';

/**
 * Checks if a dictation chunk is a voice command for the editor
 */
export const isVoiceCommandEditor = (item: ParsedDictationChunk): boolean =>
  item.type === 'SUBSTITUTION' && (item.payload as any).target === 'EDITOR';

/**
 * Checks if a dictation chunk is an external substitution
 */
export const isExternalSubstitution = (item: ParsedDictationChunk): boolean =>
  item.type === 'SUBSTITUTION' && (item.payload as any).target !== 'EDITOR';
